import React, { useRef, useState } from 'react';
import { BellOff, MessageCircle, Pin, Users } from 'lucide-react';

const ConversationList = ({
  conversations,
  selectedUser,
  employeeProfilePics,
  onSelect,
  isRTL,
  c,
  styles,
  getAvatarColor,
  isSameUser,
  t,
  theme,
  // Live presence Set — passed in by MessengerPage so the dot reflects the
  // current online state, not the stale `conv.online` captured at the moment
  // setConversations last ran. Falls back to `conv.online` if the prop wasn't
  // wired through (older call sites, etc.).
  onlineUsers,
  // Avatar-only rail mode used when the chat sidebar is collapsed.
  collapsed = false,
  // WhatsApp-style per-user prefs (all optional)
  getAlias = null,   // (chatId) => alias | null
  isPinned = null,   // (chatId) => boolean
  isMuted = null,    // (chatId) => boolean
}) => {
  const displayName = (conv) => getAlias?.(conv.id) || conv.englishName || conv.name;
  const groupScrollRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartX = useRef(0);
  const scrollStart = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  if (!conversations.length) return null;

  const isConvOnline = (conv) =>
    onlineUsers
      ? Array.from(onlineUsers).some((id) => isSameUser(id, conv.id))
      : conv.online;

  if (collapsed) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '10px 0' }}>
        {conversations.map((conv) => {
          const active = isSameUser(selectedUser?.id, conv.id);
          return (
            <div
              key={conv.id}
              onClick={() => onSelect(conv)}
              title={displayName(conv)}
              style={{
                position: 'relative',
                padding: '6px',
                borderRadius: '16px',
                cursor: 'pointer',
                background: active ? c.hoverBg : 'transparent',
                opacity: conv.isArchived ? 0.5 : 1,
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = c.hoverBg;
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <div style={{ position: 'relative', width: '46px', height: '46px' }}>
                {employeeProfilePics[conv.id] ? (
                  <img
                    src={employeeProfilePics[conv.id]}
                    alt={conv.name}
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: active ? '2px solid #2e7cba' : `2px solid ${c.border}`,
                      boxSizing: 'border-box',
                      display: 'block',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    display: employeeProfilePics[conv.id] ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: '15px',
                    background: getAvatarColor(conv.name),
                    border: active ? '2px solid #2e7cba' : '2px solid transparent',
                    boxSizing: 'border-box',
                  }}
                >
                  {String(conv.id).startsWith('group-') ? <Users size={18} /> : conv.avatar}
                </div>

                {isConvOnline(conv) && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '1px',
                      right: '1px',
                      width: '12px',
                      height: '12px',
                      background: c.online || '#22c55e',
                      border: `2px solid ${c.sidebarBg}`,
                      borderRadius: '50%',
                    }}
                  />
                )}

                {conv.unread > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      minWidth: '18px',
                      height: '18px',
                      background: '#2563eb',
                      color: '#fff',
                      fontSize: '10px',
                      borderRadius: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      padding: '0 4px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {conv.unread}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const handlePointerDown = (event) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartX.current = event.clientX;
    scrollStart.current = groupScrollRef.current?.scrollLeft || 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!isDraggingRef.current || !groupScrollRef.current) return;
    event.preventDefault();
    const delta = event.clientX - dragStartX.current;
    groupScrollRef.current.scrollLeft = scrollStart.current - delta;
  };

  const handlePointerUp = (event) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <>
      {conversations.map((conv) => (
        <div
          key={conv.id}
          onClick={() => onSelect(conv)}
          style={{
            ...styles.conversationItem,
            ...(isSameUser(selectedUser?.id, conv.id) ? styles.conversationItemActive : {}),
            ...(conv.isArchived ? { opacity: 0.5 } : {}),
          }}
          onMouseEnter={(e) => {
            if (!isSameUser(selectedUser?.id, conv.id)) {
              e.currentTarget.style.background = c.hoverBg;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSameUser(selectedUser?.id, conv.id)) {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={styles.avatarContainer}>
              {employeeProfilePics[conv.id] ? (
                <img
                  src={employeeProfilePics[conv.id]}
                  alt={conv.name}
                  style={{
                    ...styles.avatar,
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}

              <div
                style={{
                  ...styles.avatar,
                  background: getAvatarColor(conv.name),
                  display: employeeProfilePics[conv.id] ? 'none' : 'flex',
                }}
              >
                {String(conv.id).startsWith('group-') ? <Users size={18} /> : conv.avatar}
              </div>
              {/* Live presence: prefer the up-to-date Set if provided. */}
              {(onlineUsers
                ? Array.from(onlineUsers).some((id) => isSameUser(id, conv.id))
                : conv.online) && <div style={styles.onlineIndicator} />}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}
              >
                <p
                  style={{
                    fontWeight: conv.unread > 0 ? '600' : '400',
                    color: conv.unread > 0 ? c.text : c.textSecondary,
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '15px',
                  }}
                >
                  {displayName(conv)}
                  {conv.isArchived && (
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#9CA3AF' }}>(Archived)</span>
                  )}
                </p>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '12px',
                    color: conv.unread > 0 ? '#2563eb' : c.textSecondary,
                    fontWeight: conv.unread > 0 ? '600' : '400',
                    flexShrink: 0,
                    marginLeft: isRTL ? '0' : '8px',
                    marginRight: isRTL ? '8px' : '0',
                  }}
                >
                  {isMuted?.(conv.id) && <BellOff size={12} color={c.textSecondary} />}
                  {isPinned?.(conv.id) && <Pin size={12} color={c.textSecondary} />}
                  {conv.time}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p
                  style={{
                    fontSize: '14px',
                    color: conv.unread > 0 ? c.text : c.textSecondary,
                    fontWeight: conv.unread > 0 ? '500' : '400',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {conv.lastMessage || t.startConversation}
                </p>
                {conv.unread > 0 && (
                  <span
                    style={{
                      marginLeft: isRTL ? '0' : '8px',
                      marginRight: isRTL ? '8px' : '0',
                      minWidth: '22px',
                      height: '22px',
                      background: '#2563eb',
                      color: '#ffffff',
                      fontSize: '11px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      flexShrink: 0,
                      padding: '0 6px',
                    }}
                  >
                    {conv.unread}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default ConversationList;